import { Button } from "@/components/ui/button";
import { BillingPeriod } from "@/types/plans";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SubscribeButtonProps {
  selectedPlan: string | null;
  selectedPeriod: BillingPeriod | null;
  planName: string;
  price: number;
  currentPlan: string;
  isLoading: boolean;
  onSubscribe: () => void;
}

const PERIOD_SUFFIXES: Record<BillingPeriod, string> = {
  weekly: "semana",
  monthly: "mês",
  quarterly: "trimestre",
};

export const SubscribeButton = ({
  selectedPlan,
  selectedPeriod,
  planName,
  price,
  currentPlan,
  isLoading,
  onSubscribe,
}: SubscribeButtonProps) => {
  const isDisabled = !selectedPlan || !selectedPeriod || selectedPlan === currentPlan || isLoading;
  const priceInReais = price; // Price already comes in reais from hook
  const periodSuffix = selectedPeriod ? PERIOD_SUFFIXES[selectedPeriod] : "";

  const getButtonText = () => {
    if (isLoading) return "Processando...";
    if (!selectedPlan || !selectedPeriod) return "Selecione um plano";
    if (selectedPlan === currentPlan) return "Este é seu plano atual";
    if (price === 0) return `Ativar ${planName}`;
    
    // Para trimestral, mostrar preço mensal equivalente
    const displayPrice = selectedPeriod === 'quarterly' ? (price / 3).toFixed(2) : priceInReais.toFixed(2);
    const displaySuffix = selectedPeriod === 'quarterly' ? 'mês' : periodSuffix;
    
    return `Assinar ${planName} - R$${displayPrice}/${displaySuffix}`;
  };

  return (
    <div className="md:relative fixed bottom-0 left-0 right-0 pt-3 px-4 pb-[calc(48px+env(safe-area-inset-bottom)+0.25rem)] bg-gradient-to-t from-black via-black/95 to-transparent md:bg-transparent md:p-0 md:pb-0 z-50 md:border-0">
      {/* Container integrado com garantia e botão */}
      <div className="rounded-xl border border-white/10 overflow-hidden bg-black/40 backdrop-blur-sm">
        {/* Garantia integrada ao topo - mais visível */}
        <div className="bg-white/5 px-4 py-2 border-b border-white/10">
          <p className="text-center text-xs flex flex-wrap items-center justify-center gap-2">
            <span className="text-green-400 font-semibold">🛡️ 7 dias grátis</span>
            <span className="text-white/30">•</span>
            <span className="text-blue-400 font-semibold">✓ Cancele quando quiser</span>
            <span className="text-white/30">•</span>
            <span className="text-amber-400 font-semibold">🔒 100% Seguro</span>
          </p>
        </div>
        
        {/* Botão CTA */}
        <div className="p-2">
          <Button
            onClick={onSubscribe}
            disabled={isDisabled}
            variant="brand-gradient"
            className={cn(
              "w-full h-14 font-semibold text-sm sm:text-base transition-all duration-300 disabled:opacity-50 px-3",
              "hover:shadow-lg hover:shadow-luxury-pinkBright/30 hover:scale-105"
            )}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin flex-shrink-0" />}
            <span className="break-words text-center leading-tight">
              {!isDisabled && !isLoading && "🔥 "}
              {getButtonText()}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
